export type CsvValue = string | number | boolean | null | undefined;

type CsvOptions = {
  neutralizeFormulas?: boolean;
};

// Excel needs a UTF-8 byte order mark to read æ/ø/å correctly. Written as an
// escape, not a literal BOM: a raw BOM byte in a string literal desyncs Bun's
// lexer and the build fails to parse this file.
const UTF8_BOM = "\uFEFF";

/** Quote a value when it contains the delimiter, a quote, or a newline. */
export function csvEscape(
  value: CsvValue,
  delimiter = ",",
  options: CsvOptions = {},
): string {
  const raw = value === null || value === undefined ? "" : String(value);
  // Excel and similar spreadsheet programs execute formula-leading strings.
  // Opt in only for human-facing exports: machine imports require exact values.
  const s =
    options.neutralizeFormulas &&
    typeof value === "string" &&
    /^[=+\-@\t\r\n]/.test(value)
      ? `'${raw}`
      : raw;
  if (s.includes(delimiter) || s.includes('"') || /[\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Build a CSV document from a header row and body rows. */
export function toCsv(
  headers: string[],
  rows: CsvValue[][],
  delimiter = ",",
  options: CsvOptions = {},
): string {
  const lines = [
    headers.map((h) => csvEscape(h, delimiter, options)).join(delimiter),
  ];
  for (const row of rows) {
    lines.push(
      row.map((v) => csvEscape(v, delimiter, options)).join(delimiter),
    );
  }
  return lines.join("\n");
}

/**
 * Trigger a browser download of `content` as a CSV file.
 *
 * Pass `bom: true` for files a human opens in Excel — without a byte order mark
 * it renders æ/ø/å as mojibake. Leave it off for machine-consumed files (Google
 * Ads chokes on the BOM).
 */
export function downloadCsv(
  content: string,
  filename: string,
  { bom = false }: { bom?: boolean } = {},
): void {
  const blob = new Blob([bom ? UTF8_BOM + content : content], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Lowercase, dash-separated, ASCII-safe fragment for use in a filename. */
export function filenameSlug(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/æ/g, "ae")
      .replace(/ø/g, "o")
      .replace(/å/g, "a")
      // NFD splits accents off their base letter; the non-alphanumeric sweep
      // below then drops the leftover combining marks.
      .normalize("NFD")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "eksport"
  );
}
